import {useCallback, useEffect, useRef, useState} from "react";
import {useIsomorphicLayoutEffect} from "utils/useIsomorphicLayoutEffect";
import {AuthenticatedSessionState} from "../auth/AuthenticatedSessionWaiter";
import {captureFinishLinkToken, captureStopLinkToken} from "./finishLink";
import {
	confirmAvailable,
	finishAccepted,
	finishRefused,
	linkLanding,
	pressedConfirm,
	pressedStop,
	sessionSettled,
	stopAvailable,
	stopSettled,
	type SignupLinkState,
} from "./signupLinkState";
import type {StopRemindersOutcome} from "./stopReminders";
import type {FinishByLinkOutcome} from "./useEmailCodeLogin";

/**
 * What the hook needs from the outside world, injected rather than imported for the same reason
 * {@link useEmailCodeLogin} takes its `signIn`: this layer wires the rules to React and to a clock,
 * and never reaches Firebase or axios itself.
 */
export interface SignupLinkAdapters {
	finishByLink(token: string, onVerified: (email: string | null) => void): Promise<FinishByLinkOutcome>;
	stopReminders(token: string): Promise<StopRemindersOutcome>;
	authenticatedSession: AuthenticatedSessionState;
}

export interface UseSignupLink {
	state: SignupLinkState;
	/** Redeems the finish link. A no-op unless the confirm card is on offer. */
	confirm: () => void;
	/** Redeems the stop link. A no-op unless a stop token actually came with this landing. */
	stop: () => void;
}

/**
 * Drives the unfinished-signup reminder's landing on `/login` (ui#1688). Every rule lives in
 * {@link ./signupLinkState}; this hook only holds the state, reads the fragment once, and calls the api
 * when a press says to.
 *
 * **A PAGE LOAD POSTS NOTHING.** Mail scanners follow every URL in a message, so the landing may only
 * ever produce a card; both requests are behind {@link UseSignupLink.confirm} / {@link UseSignupLink.stop}.
 */
export function useSignupLink({finishByLink, stopReminders, authenticatedSession}: SignupLinkAdapters): UseSignupLink {
	// ALWAYS starts at "none", never at a token-derived state: /login is pre-rendered by SSG with no
	// fragment, so reading the token during render would make the first client render disagree with the
	// served HTML and break hydration. The layout effect below swaps in the card BEFORE the browser
	// paints, so the ordinary login modal is never actually seen by someone arriving on a reminder link.
	const stateRef = useRef<SignupLinkState>({kind: "none"});
	const [state, setState] = useState<SignupLinkState>(stateRef.current);
	const landed = useRef(false);

	// The ref, not the rendered state: a press has to read and write it synchronously, or two clicks in
	// one frame can both pass the "is this press on offer" guard and spend the link twice.
	const commit = useCallback((next: SignupLinkState) => {
		stateRef.current = next;
		setState(next);
	}, []);

	useIsomorphicLayoutEffect(() => {
		if (landed.current) {
			return;
		}
		landed.current = true;
		// Both tokens are lifted (and scrubbed from the address bar) in one go, so a finish link that
		// also carries a stop token gives the confirm card's fine print something real to press.
		commit(linkLanding(captureFinishLinkToken(), captureStopLinkToken()));
	}, [commit]);

	useEffect(() => {
		commit(sessionSettled(stateRef.current, authenticatedSession === AuthenticatedSessionState.Authenticated));
	}, [authenticatedSession, commit]);

	// GUARDED ON THE STATE, NOT ON THE TRANSITION'S RESULT. `pressedConfirm` answers the state UNCHANGED
	// when a press is not on offer, so a card already in `finishing` comes back as `finishing` — and a
	// guard that only asked "did this produce a finishing state" reads that as a fresh press and posts
	// again. `confirmAvailable` asks the question the guard actually means.
	const confirm = useCallback(() => {
		if (!confirmAvailable(stateRef.current)) {
			return;
		}
		const started = pressedConfirm(stateRef.current);
		commit(started);
		if (started.kind !== "finishing") {
			return;
		}
		void finishByLink(started.token, email => commit(finishAccepted(stateRef.current, email)))
			.then(outcome => {
				if (!outcome.signedIn) {
					commit(finishRefused(stateRef.current, outcome.failure));
				}
				// A signed-in outcome ends on the session, not here: `sessionSettled` above is what moves the
				// page on, so the app session — the thing the reader is actually waiting for — is the single
				// authority for "you are in".
			});
	}, [commit, finishByLink]);

	// Same guard, and it carries more weight here: the stop call has no single-flight beneath it, so
	// this is the only thing between a double press and two deletions.
	const stop = useCallback(() => {
		if (!stopAvailable(stateRef.current)) {
			return;
		}
		const started = pressedStop(stateRef.current);
		commit(started);
		if (started.kind !== "stopping") {
			return;
		}
		void stopReminders(started.token).then(outcome => commit(stopSettled(stateRef.current, outcome)));
	}, [commit, stopReminders]);

	return {state, confirm, stop};
}
